import { Divider, Stack } from '@mui/material';
import { compact } from 'lodash';
import { useCallback, useState } from 'react';

import { SpinningIcon } from '@/components/animation/SpinningIcon/SpinningIcon';
import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { BankIcon } from '@/components/icons/BankIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useModalState } from '@/hooks/useModalState';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { AIOnboardingModal } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal';
import {
  AIOnboardingModalVariant,
  OpenAIOnboardingModalProps,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AISuggestionReviewCard } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  CHARITABLE_ENTITY_TYPES,
  GRANTOR_ENTITY_TYPES,
} from '@/modules/entities/entities.constants';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal';
import { TestamentaryEntityModal } from '@/modules/entities/testamentaryEntities/TestamentaryEntityModal';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { useEstateOnboardingUnacknowledgedSuggestionsContext } from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import { EstateOnboardingSuggestionGroup } from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import { TrustContainer } from '@/modules/estate/EstateTrusts/TrustContainer/TrustContainer';
import {
  filterTrustsByVariant,
  getAllTrusts,
} from '@/modules/estate/EstateTrusts/TrustContainer/TrustContainer.utils';
import {
  EstatePage_EntityFragment,
  EstatePage_TestamentaryEntityFragment,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { EntityKind, EntityStage } from '@/types/schema';

import { DraftGRATsContainer } from './DraftGratsContainer/DraftGratsContainer';
import { useAutoOpenTestamentaryEntityFromDeepLink } from './hooks/useAutoOpenTestamentaryEntityFromDeepLink';
import { SubtrustsContainer } from './SubtrustsContainer/SubtrustsContainer';

interface EditTestamentaryDetails {
  testamentaryEntityId: string;
  initialSection?: EditTestamentaryEntitySection;
}

export function EstateTrusts() {
  const canEdit = useCanEditHouseholdData();
  const householdId = useRequiredParam('householdId');
  const { loading, entities, testamentaryEntities, draftEntities } =
    useEstateHoldingsContext();
  const {
    suggestionsToReviewByGroup,
    additionalReferenceSuggestions,
    refetch: refetchSuggestions,
  } = useEstateOnboardingUnacknowledgedSuggestionsContext();
  const { primaryClients } = useHouseholdDetailsContext();

  const trustEntitySuggestions =
    suggestionsToReviewByGroup[EstateOnboardingSuggestionGroup.TrustEntities] ||
    [];

  const [aiOnboardingState, aiOnboardingModal] =
    useModalState<
      Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>
    >();

  const [showCreateTrustModal, setShowCreateTrustModal] = useState(false);
  const [editTestamentaryDetails, setEditTestamentaryDetails] =
    useState<EditTestamentaryDetails | null>(null);
  useAutoOpenTestamentaryEntityFromDeepLink(setEditTestamentaryDetails);

  const handleOpenAIOnboardingModal = useCallback(
    ({
      initialSelectedSuggestion,
    }: Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>) => {
      aiOnboardingModal.openModal({ initialSelectedSuggestion });
    },
    [aiOnboardingModal]
  );

  const handleViewSubtrustDetails = useCallback(
    (subtrust: EstatePage_TestamentaryEntityFragment) => {
      setEditTestamentaryDetails({
        testamentaryEntityId: subtrust.id,
      });
    },
    []
  );

  const handleAISuggestionsClick = useCallback(
    (
      subtrust: EstatePage_TestamentaryEntityFragment,
      section: EditTestamentaryEntitySection
    ) => {
      setEditTestamentaryDetails({
        testamentaryEntityId: subtrust.id,
        initialSection: section,
      });
    },
    []
  );

  const allTrusts: EstatePage_EntityFragment[] = getAllTrusts(
    compact(entities)
  );
  const allTestamentaryEntities: EstatePage_TestamentaryEntityFragment[] =
    compact(testamentaryEntities);
  const draftGRATs: EstatePage_EntityFragment[] = compact(draftEntities).filter(
    (entity) => {
      return (
        entity.kind === EntityKind.GratTrust &&
        [
          EntityStage.Draft,
          EntityStage.ReadyForProposal,
          EntityStage.Implementation,
        ].includes(entity.stage)
      );
    }
  );

  const hasExistingData =
    (entities?.length ?? 0) > 0 ||
    (testamentaryEntities?.length ?? 0) > 0 ||
    (draftEntities?.length ?? 0) > 0;

  if (loading && !hasExistingData) {
    return (
      <Stack
        height="40px"
        direction="row"
        alignItems="center"
        justifyContent="center"
      >
        <SpinningIcon sx={{ color: COLORS.TEXT.SECONDARY }} />
      </Stack>
    );
  }

  // Show suggestions even if there are no existing trusts
  const hasTrustSuggestions = trustEntitySuggestions.length > 0;

  // Show empty state only when there are no trusts, testamentary entities, or draft entities
  const shouldShowEmptyState =
    allTrusts.length === 0 &&
    allTestamentaryEntities.length === 0 &&
    draftGRATs.length === 0;

  // Show containers when testamentary entities or draft entities exist
  const shouldShowSubtrustsOrDraftContainers =
    allTestamentaryEntities.length > 0 || draftGRATs.length > 0;

  // Check which variants have trusts to display
  const inEstateTrusts = filterTrustsByVariant(compact(entities), 'in-estate');
  const familyTrusts = filterTrustsByVariant(
    compact(entities),
    'out-of-estate-family'
  );
  const charityTrusts = filterTrustsByVariant(
    compact(entities),
    'out-of-estate-charity'
  );

  const shouldShowTrustsContainers =
    inEstateTrusts.length > 0 ||
    familyTrusts.length > 0 ||
    charityTrusts.length > 0;

  return (
    <Stack spacing={5}>
      {hasTrustSuggestions && canEdit && (
        <AISuggestionReviewCard
          variant={AIOnboardingModalVariant.TRUST_ENTITIES}
          suggestionDisplayText={{
            singular: 'trust',
            plural: 'trusts',
          }}
          suggestions={trustEntitySuggestions}
          additionalReferenceSuggestions={additionalReferenceSuggestions}
          onOpenAIOnboardingModal={handleOpenAIOnboardingModal}
          // No need to show the processing banner here since the whole
          // "/estate" outlet already has one in EstateOnboardingSuggestionsBanner
          showProcessingBanner={false}
          onSuggestionCreated={refetchSuggestions}
        />
      )}
      {shouldShowEmptyState ? (
        <SmallTableEmptyState
          fill="viewport"
          icon={<BankIcon size={32} color={COLORS.TEXT.PRIMARY} />}
          heading="No trusts"
          description="There are no trusts associated with this client"
          action={
            canEdit ? (
              <Button
                variant="primary"
                size="sm"
                startIcon={PlusIcon}
                onClick={() => setShowCreateTrustModal(true)}
              >
                Create trust
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Stack
          divider={<Divider sx={{ borderColor: `${COLORS.GRAY['200']}` }} />}
          spacing="40px"
        >
          {shouldShowTrustsContainers && (
            <Stack spacing="40px">
              {inEstateTrusts.length > 0 && (
                <TrustContainer variant={'in-estate'} />
              )}
              {familyTrusts.length > 0 && (
                <TrustContainer variant={'out-of-estate-family'} />
              )}
              {charityTrusts.length > 0 && (
                <TrustContainer variant={'out-of-estate-charity'} />
              )}
            </Stack>
          )}
          {shouldShowSubtrustsOrDraftContainers && (
            <Stack spacing={2}>
              {allTestamentaryEntities.length > 0 && (
                <SubtrustsContainer
                  subtrusts={allTestamentaryEntities}
                  onViewDetails={handleViewSubtrustDetails}
                  onAISuggestionsClick={handleAISuggestionsClick}
                />
              )}
              {draftGRATs.length > 0 && (
                <DraftGRATsContainer draftEntities={draftGRATs} />
              )}
            </Stack>
          )}
        </Stack>
      )}
      {aiOnboardingState.isModalOpen && (
        <AIOnboardingModal
          isOpen={aiOnboardingState.isModalOpen}
          onClose={() => aiOnboardingModal.closeModal()}
          householdId={householdId}
          variant={AIOnboardingModalVariant.TRUST_ENTITIES}
          initialSelectedSuggestion={
            aiOnboardingState.data?.initialSelectedSuggestion
          }
          suggestionsWhere={{
            idIn: trustEntitySuggestions.map((suggestion) => suggestion.id),
            acceptanceStatusIsNil: true,
          }}
          additionalReferenceSuggestions={additionalReferenceSuggestions}
          skipNavigationOnComplete={true}
        />
      )}
      {editTestamentaryDetails && (
        <TestamentaryEntityModal
          isOpen={!!editTestamentaryDetails}
          onClose={() => setEditTestamentaryDetails(null)}
          testamentaryEntityId={editTestamentaryDetails.testamentaryEntityId}
          deadClientIds={[]}
          isTwoClientHousehold={(primaryClients ?? []).length === 2}
          initialSection={editTestamentaryDetails.initialSection}
        />
      )}
      {showCreateTrustModal && (
        <CreateEntityShortFormModal
          isOpen={showCreateTrustModal}
          onClose={() => setShowCreateTrustModal(false)}
          householdId={householdId}
          limitEntityTypes={[
            ...GRANTOR_ENTITY_TYPES,
            ...CHARITABLE_ENTITY_TYPES,
          ]}
          heading="Create trust"
          entityTypeLabel="Trust type"
          buttonText="Create trust"
        />
      )}
    </Stack>
  );
}
